import CryptoJS from "crypto-js";

console.log("NODE_ENV : ", process.env.NODE_ENV);
/**
 * Console Logger Utility
 * Captures console.log, console.error, console.warn, console.info messages,
 * uncaught JavaScript errors, unhandled promise rejections, and failed HTTP requests.
 * Provides functionality to save them to a text file and upload to Azure
 */

class ConsoleLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 10000; // Limit to prevent memory issues
    this.isEnabled = true;
    this.sessionInfo = {
      meetingId: null,
      candidateName: null,
      sessionStartTime: new Date().toISOString(),
    };

    // Store original console methods
    this.originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info,
    };

    this.initializeLogger();
    this.initializeErrorHandlers();
    this.initializeNetworkInterceptors();
  }

  initializeLogger() {
    const self = this;

    // Override console.log
    console.log = function (...args) {
      self.captureLog("LOG", args);
      if (process.env.NODE_ENV !== "production") {
        self.originalConsole.log.apply(console, args);
      }
    };

    // Override console.error
    console.error = function (...args) {
      self.captureLog("ERROR", args);
      if (process.env.NODE_ENV !== "production") {
        self.originalConsole.error.apply(console, args);
      }
    };

    // Override console.warn
    console.warn = function (...args) {
      self.captureLog("WARN", args);
      if (process.env.NODE_ENV !== "production") {
        self.originalConsole.warn.apply(console, args);
      }
    };

    // Override console.info
    console.info = function (...args) {
      self.captureLog("INFO", args);
      self.originalConsole.info.apply(console, args);
    };
  }

  initializeErrorHandlers() {
    const self = this;

    // Handle uncaught JavaScript errors
    window.addEventListener("error", function (event) {
      const errorInfo = {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error ? event.error.stack : "No stack trace available",
      };

      const errorMessage = `Uncaught Error: ${errorInfo.message} at ${errorInfo.filename}:${errorInfo.lineno}:${errorInfo.colno}\nStack: ${errorInfo.stack}`;
      self.captureLog("UNCAUGHT_ERROR", [errorMessage]);
    });

    // Handle unhandled promise rejections
    window.addEventListener("unhandledrejection", function (event) {
      const reason = event.reason;
      let errorMessage = "Unhandled Promise Rejection: ";

      if (reason instanceof Error) {
        errorMessage += `${reason.message}\nStack: ${reason.stack}`;
      } else if (typeof reason === "string") {
        errorMessage += reason;
      } else {
        try {
          errorMessage += JSON.stringify(reason, null, 2);
        } catch (e) {
          errorMessage += String(reason);
        }
      }

      self.captureLog("UNHANDLED_REJECTION", [errorMessage]);
    });
  }

  initializeNetworkInterceptors() {
    const self = this;

    // Store original fetch
    const originalFetch = window.fetch;

    // Override fetch to capture failed requests
    window.fetch = function (...args) {
      const [url, options = {}] = args;
      const method = options.method || "GET";
      const startTime = Date.now();

      return originalFetch
        .apply(this, args)
        .then((response) => {
          const duration = Date.now() - startTime;

          // Log failed HTTP responses (4xx, 5xx)
          if (!response.ok) {
            const errorMessage = `HTTP ${method} ${url} failed with status ${response.status} ${response.statusText} (${duration}ms)`;
            self.captureLog("HTTP_ERROR", [errorMessage]);
          }

          return response;
        })
        .catch((error) => {
          const duration = Date.now() - startTime;
          const errorMessage = `HTTP ${method} ${url} network error: ${error.message} (${duration}ms)`;
          self.captureLog("NETWORK_ERROR", [errorMessage]);
          throw error; // Re-throw to maintain original behavior
        });
    };

    // Store original XMLHttpRequest
    const originalXHR = window.XMLHttpRequest;

    // Override XMLHttpRequest to capture failed requests
    window.XMLHttpRequest = function () {
      const xhr = new originalXHR();
      const originalOpen = xhr.open;
      const originalSend = xhr.send;

      let method, url, startTime;

      xhr.open = function (m, u, ...args) {
        method = m;
        url = u;
        return originalOpen.apply(this, [m, u, ...args]);
      };

      xhr.send = function (...args) {
        startTime = Date.now();

        // Add event listeners for error handling
        xhr.addEventListener("error", function () {
          const duration = Date.now() - startTime;
          const errorMessage = `XHR ${method} ${url} network error (${duration}ms)`;
          self.captureLog("NETWORK_ERROR", [errorMessage]);
        });

        xhr.addEventListener("load", function () {
          const duration = Date.now() - startTime;

          // Log failed HTTP responses (4xx, 5xx)
          if (xhr.status >= 400) {
            const errorMessage = `XHR ${method} ${url} failed with status ${xhr.status} ${xhr.statusText} (${duration}ms)`;
            self.captureLog("HTTP_ERROR", [errorMessage]);
          }
        });

        return originalSend.apply(this, args);
      };

      return xhr;
    };

    // Preserve the original constructor properties
    Object.setPrototypeOf(window.XMLHttpRequest, originalXHR);
    Object.setPrototypeOf(
      window.XMLHttpRequest.prototype,
      originalXHR.prototype
    );
  }

  // Set interview session information
  setSessionInfo(meetingId, candidateName) {
    this.sessionInfo.meetingId = meetingId;
    this.sessionInfo.candidateName = candidateName;
    this.sessionInfo.sessionStartTime = new Date().toISOString();

    // Log session start
    this.captureLog("INFO", [
      `Interview session started - Meeting ID: ${meetingId}, Candidate: ${candidateName}`,
    ]);
  }

  captureLog(level, args) {
    if (!this.isEnabled) return;

    const timestamp = new Date().toISOString();
    const message = args
      .map((arg) => {
        if (typeof arg === "object") {
          try {
            return JSON.stringify(arg, null, 2);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(" ");

    const logEntry = {
      timestamp,
      level,
      message,
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionInfo: { ...this.sessionInfo },
    };

    this.logs.push(logEntry);

    // Remove old logs if we exceed the limit
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  // Generate interview-specific filename
  generateInterviewLogFilename(meetingId) {
    // const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    // const { meetingId, candidateName } = this.sessionInfo;

    // const safeCandidateName = candidateName ? candidateName.replace(/[^a-zA-Z0-9]/g, '_') : 'unknown';
    // const safeMeetingId = meetingId || 'no-meeting-id';

    return `browser_logs_${CryptoJS.MD5(meetingId)}.txt`;
  }

  // Get logs formatted for interview session
  getInterviewLogsAsText() {
    const header = `
=== INTERVIEW SESSION LOGS ===
Meeting ID: ${this.sessionInfo.meetingId || "N/A"}
Candidate: ${this.sessionInfo.candidateName || "N/A"}
Session Start: ${this.sessionInfo.sessionStartTime}
Log Generated: ${new Date().toISOString()}
Total Logs: ${this.logs.length}
===================================

`;

    const logText = this.logs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.message}`)
      .join("\n");

    return header + logText;
  }

  // Create blob for interview logs
  createInterviewLogBlob() {
    const logText = this.getInterviewLogsAsText();
    return new Blob([logText], { type: "text/plain" });
  }

  // Enable/disable logging
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }

  // Clear all captured logs
  clearLogs() {
    this.logs = [];
  }

  // Get all logs
  getLogs() {
    return [...this.logs];
  }

  // Get logs as formatted text
  getLogsAsText() {
    return this.logs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.message}`)
      .join("\n");
  }

  // Download logs as a text file
  downloadLogs(filename = null) {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const defaultFilename = `console-logs-${timestamp}.txt`;
    const finalFilename = filename || defaultFilename;

    const logText = this.getLogsAsText();
    const blob = new Blob([logText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);

    return finalFilename;
  }

  // Download interview-specific logs
  downloadInterviewLogs() {
    const filename = this.generateInterviewLogFilename();
    const logText = this.getInterviewLogsAsText();
    const blob = new Blob([logText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);

    return filename;
  }

  // Save logs to localStorage (as backup)
  saveToLocalStorage(key = "console-logs") {
    try {
      localStorage.setItem(key, JSON.stringify(this.logs));
      return true;
    } catch (e) {
      console.error("Failed to save logs to localStorage:", e);
      return false;
    }
  }

  // Load logs from localStorage
  loadFromLocalStorage(key = "console-logs") {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        this.logs = JSON.parse(saved);
        return true;
      }
    } catch (e) {
      console.error("Failed to load logs from localStorage:", e);
    }
    return false;
  }

  // Get logs filtered by level
  getLogsByLevel(level) {
    return this.logs.filter((log) => log.level === level.toUpperCase());
  }

  // Get all error-related logs (ERROR, UNCAUGHT_ERROR, UNHANDLED_REJECTION, HTTP_ERROR, NETWORK_ERROR)
  getErrorLogs() {
    return this.logs.filter((log) =>
      [
        "ERROR",
        "UNCAUGHT_ERROR",
        "UNHANDLED_REJECTION",
        "HTTP_ERROR",
        "NETWORK_ERROR",
      ].includes(log.level)
    );
  }

  // Get only network-related error logs (HTTP_ERROR, NETWORK_ERROR)
  getNetworkErrorLogs() {
    return this.logs.filter((log) =>
      ["HTTP_ERROR", "NETWORK_ERROR"].includes(log.level)
    );
  }

  // Get logs within a time range
  getLogsByTimeRange(startTime, endTime) {
    const start = new Date(startTime);
    const end = new Date(endTime);

    return this.logs.filter((log) => {
      const logTime = new Date(log.timestamp);
      return logTime >= start && logTime <= end;
    });
  }

  // Restore original console methods
  restoreConsole() {
    console.log = this.originalConsole.log;
    console.error = this.originalConsole.error;
    console.warn = this.originalConsole.warn;
    console.info = this.originalConsole.info;
  }

  // Get statistics about logs
  getLogStats() {
    const stats = {
      total: this.logs.length,
      byLevel: {
        LOG: 0,
        ERROR: 0,
        WARN: 0,
        INFO: 0,
        UNCAUGHT_ERROR: 0,
        UNHANDLED_REJECTION: 0,
        HTTP_ERROR: 0,
        NETWORK_ERROR: 0,
      },
    };

    this.logs.forEach((log) => {
      if (stats.byLevel.hasOwnProperty(log.level)) {
        stats.byLevel[log.level]++;
      }
    });

    return stats;
  }
}

// Create a singleton instance
const consoleLogger = new ConsoleLogger();

// Export the instance and class
export default consoleLogger;
export { ConsoleLogger };

// Global functions for easy access
window.downloadConsoleLogs = (filename) => consoleLogger.downloadLogs(filename);
window.downloadInterviewLogs = () => consoleLogger.downloadInterviewLogs();
window.clearConsoleLogs = () => consoleLogger.clearLogs();
window.getConsoleLogStats = () => consoleLogger.getLogStats();
window.saveConsoleLogs = () => consoleLogger.saveToLocalStorage();
window.setInterviewSession = (meetingId, candidateName) =>
  consoleLogger.setSessionInfo(meetingId, candidateName);
window.getErrorLogs = () => consoleLogger.getErrorLogs();
window.getNetworkErrorLogs = () => consoleLogger.getNetworkErrorLogs();
